import { useEffect, useState, useRef, useCallback } from "react";
import { ThreeDot } from "react-loading-indicators";
import { usePaths } from "BEDROCK/paths";
import { useToast } from "BEDROCK/shared-components/toast";
import { useStravaActivities } from "../hooks/use-strava-activities";
import { useStravaPreviewData } from "../hooks/use-strava-preview-data";
import { useStravaDestination } from "../hooks/use-strava-destination";
import { useAnalyticsTracker } from "analytics-tracker";
import {
  StravaActivityList,
  ActivitySelectedResult,
} from "../components/strava-activity-list";
import {
  StravaPreviewData,
  StravaAnalyticsEvents,
  StravaActivitiesErrorTypes,
} from "../types";
import { DEFAULT_COLLECTION_LIST_HANDLE } from "../constants";
import styles from "./strava-activities-page.module.css";

type PageState =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "error"; message: string };

/**
 * Activity selection page for the Strava acquisition flow.
 * Displays a grid of recent activities from the user's Strava account
 * and allows them to select one to turn into a poster.
 *
 * Uses the shared StravaActivityList component for the activity list display
 * with filter chips and handles navigation to the collection page after selection.
 */
export const StravaActivitiesPage = () => {
  const { navigateTo, stravaLanding } = usePaths();
  const { getActivitiesData } = useStravaActivities();
  const { savePreviewData } = useStravaPreviewData();
  const { getDestination, clearDestination } = useStravaDestination();
  const { trackEvent } = useAnalyticsTracker();
  const { showToast } = useToast();

  const [state, setState] = useState<PageState>({ status: "loading" });
  const hasLoadedRef = useRef(false);

  // Check for activities data on mount
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const activitiesData = getActivitiesData();

    if (!activitiesData) {
      trackEvent(StravaAnalyticsEvents.ACTIVITIES_ERROR, {
        error_type: StravaActivitiesErrorTypes.NO_DATA,
      });
      setState({
        status: "error",
        message: "No activities found. Please connect to Strava again.",
      });
      return;
    }

    if (activitiesData.activities.length === 0) {
      trackEvent(StravaAnalyticsEvents.ACTIVITIES_ERROR, {
        error_type: StravaActivitiesErrorTypes.EMPTY_LIST,
      });
      setState({
        status: "error",
        message: "No activities with GPS data found in your Strava account.",
      });
      return;
    }

    trackEvent(StravaAnalyticsEvents.ACTIVITIES_VIEWED, {
      activity_count: activitiesData.activities.length,
    });

    setState({ status: "ready" });
  }, [getActivitiesData, trackEvent]);

  const handleActivitySelected = useCallback(
    (result: ActivitySelectedResult) => {
      trackEvent(StravaAnalyticsEvents.ACTIVITY_SELECTED, {
        activity_type: result.activity.type,
        activity_id: result.activity.id,
      });

      // Create preview data to pass through template selection
      const previewData: StravaPreviewData = {
        activityId: result.activity.id,
        activityName: result.activity.name,
        activityType: result.activity.type,
        startDate: result.activity.start_date_local,
        coordinates: result.coordinates,
        gpsData: result.gpsData,
        athleteFirstName: result.athleteFirstName,
        fetchedAt: Date.now(),
        stravaDistance: result.activity.distance,
        stravaElevationGain: result.activity.total_elevation_gain,
        stravaMovingTime: result.activity.moving_time,
      };

      // Save preview data for the collection page to use
      savePreviewData(previewData);

      // Keep activities data - it contains the access token needed for the editor
      // to show "Connected to Strava" state. Only clear on explicit disconnect.

      // Navigate using destination from localStorage
      const { destination, wasExpired } = getDestination();
      clearDestination();

      // Notify user if their intended destination expired
      if (wasExpired) {
        showToast(
          "Your session took a while, so we're showing you all styles instead.",
          "info",
          6000
        );
      }

      if (destination?.collectionId) {
        navigateTo(`/collection/${destination.collectionId}`);
      } else {
        const handle = destination?.collectionListHandle ?? DEFAULT_COLLECTION_LIST_HANDLE;
        navigateTo(`/collection-list/${handle}`);
      }
    },
    [
      trackEvent,
      savePreviewData,
      navigateTo,
      getDestination,
      clearDestination,
      showToast,
    ],
  );

  const handleActivityApplied = useCallback(() => {
    // Navigation is handled in handleActivitySelected
  }, []);

  const handleRetry = () => {
    navigateTo(stravaLanding);
  };

  return (
    <div className={styles.page}>
      <main className={styles.main}>
        {state.status === "loading" && <LoadingState />}

        {state.status === "ready" && (
          <StravaActivityList
            onActivityApplied={handleActivityApplied}
            onActivitySelected={handleActivitySelected}
          />
        )}

        {state.status === "error" && (
          <ErrorState message={state.message} onRetry={handleRetry} />
        )}
      </main>
    </div>
  );
};

const LoadingState = () => (
  <div className={styles.centered}>
    <ThreeDot color="var(--color-primary)" size="medium" text="" textColor="" />
    <p className={styles.loadingMessage}>Loading your activities...</p>
  </div>
);

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

const ErrorState = ({ message, onRetry }: ErrorStateProps) => (
  <div className={styles.centered}>
    <div className={styles.errorIcon}>!</div>
    <p className={styles.errorMessage}>{message}</p>
    <button className={styles.retryButton} onClick={onRetry}>
      Try Again
    </button>
  </div>
);
